import { ChangeDetectionStrategy, Component, inject, input } from '@angular/core';
import { FormBuilder, ReactiveFormsModule, Validators } from '@angular/forms';

import { WaitlistService } from '../../core/services/waitlist.service';

/**
 * Signup form shown on the gate while the partner workspace is offline.
 *
 * Single opt-in: there is no confirmation email, so the ticked consent box is
 * the whole record of agreement. It is never pre-ticked, since NDPR consent
 * has to be an affirmative act to count for anything.
 */
@Component({
  selector: 'app-gate-waitlist-form',
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [ReactiveFormsModule],
  styles: [
    `
      .wl-input {
        width: 100%;
        border-radius: 0.5rem;
        border: 1px solid var(--border-default);
        background: var(--card-background);
        color: var(--text-primary);
        padding: 0.65rem 0.9rem;
        font-size: 0.875rem;
        outline: none;
        transition: border-color 0.15s, box-shadow 0.15s;
      }
      .wl-input::placeholder {
        color: var(--text-secondary);
      }
      .wl-input:focus {
        border-color: var(--brand-primary);
        box-shadow: 0 0 0 3px color-mix(in srgb, var(--brand-primary) 15%, transparent);
      }
      .wl-btn {
        color: #fff;
        border-radius: 0.5rem;
        font-weight: 600;
        transition: background-color 0.15s, transform 0.1s;
      }
      .wl-btn:active:not(:disabled) {
        transform: translateY(1px);
      }
      .wl-btn-primary {
        background: var(--brand-primary);
      }
      .wl-btn-primary:hover:not(:disabled) {
        background: var(--brand-primary-hover);
      }
    `,
  ],
  template: `
    @if (waitlist.state() === 'success') {
      <div class="text-center py-6" role="status">
        <div
          class="size-12 rounded-full mx-auto flex items-center justify-center text-white"
          style="background: var(--status-success)"
        >
          <svg viewBox="0 0 24 24" fill="none" class="w-6 h-6">
            <path
              d="M5 13l4 4L19 7"
              stroke="currentColor"
              stroke-width="2.5"
              stroke-linecap="round"
              stroke-linejoin="round"
            />
          </svg>
        </div>
        <p class="font-semibold mt-4" style="color: var(--brand-primary)">You're on the list</p>
        <p class="text-sm mt-1" style="color: var(--text-secondary)">
          We'll email you at {{ submittedEmail }} as soon as the workspace opens.
        </p>
      </div>
    } @else {
      <form [formGroup]="form" (ngSubmit)="submit()" novalidate class="w-full text-left">
        <div class="space-y-3">
          <div>
            <label class="sr-only" for="wl-organization">Organization</label>
            <input
              id="wl-organization"
              type="text"
              formControlName="organization"
              autocomplete="organization"
              placeholder="Organization (optional)"
              class="wl-input"
            />
          </div>

          <div>
            <label class="sr-only" for="wl-name">Full name</label>
            <input
              id="wl-name"
              type="text"
              formControlName="name"
              autocomplete="name"
              placeholder="Full Name"
              [attr.aria-invalid]="isInvalid('name') ? 'true' : null"
              class="wl-input"
            />
            @if (isInvalid('name')) {
              <p class="text-xs mt-1" style="color: var(--status-danger)">Please tell us your name.</p>
            }
          </div>

          <div>
            <label class="sr-only" for="wl-email">Email address</label>
            <input
              id="wl-email"
              type="email"
              formControlName="email"
              autocomplete="email"
              inputmode="email"
              placeholder="Email Address"
              [attr.aria-invalid]="isInvalid('email') ? 'true' : null"
              class="wl-input"
            />
            @if (isInvalid('email')) {
              <p class="text-xs mt-1" style="color: var(--status-danger)">
                Please enter an email address we can reach you at.
              </p>
            }
          </div>

          <div>
            <label class="sr-only" for="wl-phone">Phone number</label>
            <input
              id="wl-phone"
              type="tel"
              formControlName="phone"
              autocomplete="tel"
              inputmode="tel"
              placeholder="Phone Number (optional)"
              [attr.aria-invalid]="isInvalid('phone') ? 'true' : null"
              class="wl-input"
            />
            @if (isInvalid('phone')) {
              <p class="text-xs mt-1" style="color: var(--status-danger)">Please enter a valid phone number.</p>
            }
          </div>
        </div>

        <div class="hidden" aria-hidden="true">
          <label for="wl-website">Website</label>
          <input id="wl-website" type="text" formControlName="website" tabindex="-1" autocomplete="off" />
        </div>

        @if (consentText()) {
          <div class="flex items-start gap-2 mt-3">
            <input
              id="wl-consent"
              type="checkbox"
              formControlName="consent"
              [attr.aria-invalid]="isInvalid('consent') ? 'true' : null"
              class="mt-0.5 h-4 w-4 shrink-0 rounded"
              style="accent-color: var(--brand-primary)"
            />
            <label for="wl-consent" class="text-[11px] leading-snug" style="color: var(--text-secondary)">
              {{ consentText() }}
            </label>
          </div>
          @if (isInvalid('consent')) {
            <p class="text-xs mt-1" style="color: var(--status-danger)">
              Please tick the box so we know you're happy to hear from us.
            </p>
          }
        }

        <button
          type="submit"
          [disabled]="waitlist.state() === 'submitting'"
          class="wl-btn wl-btn-primary w-full mt-3 px-6 py-2.5 text-sm disabled:opacity-70"
        >
          @if (waitlist.state() === 'submitting') {
            Joining…
          } @else {
            Notify Me on Launch
          }
        </button>

        @if (waitlist.state() === 'error') {
          <p class="text-sm mt-3 text-center" style="color: var(--status-danger)">{{ waitlist.error() }}</p>
        }
      </form>
    }
  `,
})
export class GateWaitlistForm {
  private readonly fb = inject(FormBuilder);
  readonly waitlist = inject(WaitlistService);

  readonly consentText = input<string>('');

  readonly form = this.fb.nonNullable.group({
    organization: [''],
    name: ['', [Validators.required, Validators.maxLength(255)]],
    email: ['', [Validators.required, Validators.email, Validators.maxLength(255)]],
    phone: ['', [Validators.maxLength(30), Validators.pattern(/^[+0-9\s().-]{7,30}$/)]],
    consent: [false, [Validators.requiredTrue]],
    website: [''],
  });

  submittedEmail = '';

  isInvalid(control: keyof typeof this.form.controls): boolean {
    const field = this.form.controls[control];
    return field.invalid && (field.touched || field.dirty);
  }

  submit(): void {
    if (this.waitlist.state() === 'submitting') {
      return;
    }
    if (this.form.invalid) {
      this.form.markAllAsTouched();
      return;
    }

    const { organization, name, email, phone, consent } = this.form.getRawValue();
    this.submittedEmail = email;

    this.waitlist
      .join({ organization: organization || undefined, name, email, phone: phone || undefined, consent })
      .subscribe({ error: () => undefined });
  }
}
